import { useEffect, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";

/** Admin: free users who met an earned-trial condition but never got a trial. */
const AdminMissedTrialsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [granting, setGranting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get("/admin/missed-trials");
      setRows(d.users || []);
      setSelected(new Set());
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));

  const grant = async (ids) => {
    if (ids.length === 0) return;
    setGranting(true);
    try {
      const res = await api.post("/admin/missed-trials/grant", { user_ids: ids });
      toast.success(`Granted ${res.granted_count} trial(s)`);
      await load();
    } catch { toast.error("Grant failed"); }
    finally { setGranting(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

  return (
    <div data-testid="admin-missed-trials" className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Missed earned trials</h1>
        <p className="text-sm text-muted-foreground">Free users who completed the readiness check or answered 20+ questions but never got the 7-day Pro trial. Granting sends no email.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="missed-empty">No users are missing a trial right now. 🎉</p>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={toggleAll} data-testid="missed-select-all" className="rounded-full">
              {selected.size === rows.length ? "Clear" : "Select all"}
            </Button>
            <Button size="sm" disabled={selected.size === 0 || granting} onClick={() => grant([...selected])} data-testid="missed-grant-selected" className="rounded-full">
              <Gift className="w-4 h-4 mr-1.5" /> Grant to {selected.size} selected
            </Button>
          </div>
          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left">
                <tr>
                  <th className="p-3 w-8"></th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Signed up</th>
                  <th className="p-3">Readiness</th>
                  <th className="p-3">Answered</th>
                  <th className="p-3">Met on</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-t border-border" data-testid={`missed-row-${u.id}`}>
                    <td className="p-3"><input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} data-testid={`missed-check-${u.id}`} /></td>
                    <td className="p-3 font-medium">{u.email}</td>
                    <td className="p-3 text-muted-foreground">{fmt(u.signup_date)}</td>
                    <td className="p-3">{u.readiness_completed ? `Yes · ${fmt(u.readiness_at)}` : "No"}</td>
                    <td className="p-3">{u.questions_answered}</td>
                    <td className="p-3 text-muted-foreground">{fmt(u.condition_met_at)}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" disabled={granting} onClick={() => grant([u.id])} data-testid={`missed-grant-${u.id}`} className="rounded-full">
                        Grant 7-day trial
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMissedTrialsPage;
